import { useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { DndContext, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core'
import type { DragEndEvent } from '@dnd-kit/core'
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable'
import { useCommercialInvoices, type CommercialInvoice } from '../../../hooks/useCommercialInvoices'
import { useLocalColumnPrefs } from '../../../hooks/useLocalColumnPrefs'
import { SortableColHeader, ColumnToggleButton } from '../../Warehouse/ColDragHeader'
import CustomerCombobox from '../../../components/CustomerCombobox'

const STATUS_STYLES: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  confirmed: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  filed: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
}

// Z77 revised: reorder + show/hide only -- no resize, no sticky horizontal scroll (this table's
// current width doesn't overflow, so there's nothing for a scrollbar to help with).
const ALL_COLS = [
  { id: 'invoice_no', label: 'Invoice No' },
  { id: 'consignee', label: 'Consignee' },
  { id: 'shipper', label: 'Shipper' },
  { id: 'date', label: 'Date' },
  { id: 'amount', label: 'Amount' },
  { id: 'job_ref', label: 'Job Ref' },
  { id: 'status', label: 'Status' },
  { id: 'report', label: 'Report' },
  { id: 'source', label: 'Source' },
] as const

const COL_HEADER_ALIGN: Record<string, string> = {
  invoice_no: 'text-left', consignee: 'text-left', shipper: 'text-left', date: 'text-left',
  amount: 'text-right', job_ref: 'text-center', status: 'text-center', report: 'text-center', source: 'text-center',
}

// Keyed the same way as ALL_COLS so both <thead> and <tbody> stay driven by the same
// visibleCols/order -- hiding or reordering a column actually hides/reorders its data cells too,
// not just the header (unlike the header-only wiring some other tables in this app have).
const CELL_RENDERERS: Record<string, (inv: CommercialInvoice) => { className: string; content: ReactNode }> = {
  invoice_no: inv => ({ className: 'px-4 py-3 font-mono text-blue-600 dark:text-blue-400', content: inv.invoice_no ?? '—' }),
  consignee: inv => ({ className: 'px-4 py-3 text-gray-900 dark:text-white font-medium', content: inv.consignee ?? '—' }),
  shipper: inv => ({ className: 'px-4 py-3 text-gray-600 dark:text-gray-300', content: inv.shipper ?? '—' }),
  date: inv => ({ className: 'px-4 py-3 text-gray-600 dark:text-gray-300', content: inv.invoice_date ?? '—' }),
  amount: inv => ({
    className: 'px-4 py-3 text-right font-mono text-gray-900 dark:text-white',
    content: inv.total_amount != null ? `${inv.currency} ${inv.total_amount.toLocaleString()}` : '—',
  }),
  job_ref: inv => ({ className: 'px-4 py-3 text-center font-mono text-sm text-gray-900 dark:text-white', content: inv.job_ref ?? '—' }),
  status: inv => ({
    className: 'px-4 py-3 text-center',
    content: (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[inv.status] ?? STATUS_STYLES.draft}`}>
        {inv.status}
      </span>
    ),
  }),
  report: inv => ({
    className: 'px-4 py-3 text-center',
    content: inv.drive_folder_url ? (
      <a href={inv.drive_folder_url} target="_blank" rel="noopener noreferrer"
        onClick={e => e.stopPropagation()}
        className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium">
        📁 View
      </a>
    ) : <span className="text-gray-400">—</span>,
  }),
  source: inv => ({
    className: 'px-4 py-3 text-center',
    content: inv.source_type === 'pdf' ? (
      <span className="px-2 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded-full text-xs">
        AI {inv.ai_confidence != null ? `${Math.round(inv.ai_confidence * 100)}%` : ''}
      </span>
    ) : null,
  }),
}

export default function CommercialInvoiceIndex() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [customerId, setCustomerId] = useState('')
  const { invoices, loading, error, stats } = useCommercialInvoices(search, customerId)

  const { order: colOrder, hidden: hiddenCols, visibleCols, setOrder: setColOrder, toggleHidden: toggleCol, reset: resetCols } =
    useLocalColumnPrefs('commercial_invoices_column_prefs', ALL_COLS)
  const colSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  function handleColDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (over && active.id !== over.id) {
      const oldIndex = colOrder.indexOf(String(active.id))
      const newIndex = colOrder.indexOf(String(over.id))
      if (oldIndex !== -1 && newIndex !== -1) setColOrder(arrayMove(colOrder, oldIndex, newIndex))
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 mb-1">
          <button
            onClick={() => navigate('/customs')}
            className="hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            Customs
          </button>
          <span>/</span>
          <span className="text-gray-900 dark:text-white">Commercial Invoices</span>
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Commercial Invoices</h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">AI-parsed packing lists and commercial invoices</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/customs/invoices/templates')}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.5a2 2 0 00-1 .267M7 21H5a2 2 0 01-2-2v-4a2 2 0 012-2h2.5" />
              </svg>
              Templates
            </button>
            <button
              onClick={() => navigate('/customs/invoices/upload')}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              Upload & Parse
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'Total', value: stats.total, color: 'gray' },
            { label: 'Draft', value: stats.draft, color: 'gray' },
            { label: 'Confirmed', value: stats.confirmed, color: 'blue' },
            { label: 'Filed', value: stats.filed, color: 'green' },
            { label: 'AI Parsed', value: stats.aiParsed, color: 'purple' },
          ].map(s => (
            <div key={s.label} className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-white">{s.value}</div>
              <div className="text-sm text-gray-500 dark:text-gray-400">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Search + Customer filter */}
        <div className="mb-4 flex gap-3">
          <input
            type="text"
            placeholder="Search by consignee, invoice no, or shipper..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <div className="w-64">
            <CustomerCombobox value={customerId} onChange={setCustomerId} role="Cus" placeholder="All customers" />
          </div>
          <ColumnToggleButton cols={ALL_COLS} hidden={hiddenCols} onToggle={toggleCol} onReset={resetCols} />
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading invoices...</div>
        ) : error ? (
          <div className="text-center py-12 text-red-500">Error: {error}</div>
        ) : invoices.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400 mb-4">No invoices found</p>
            <button
              onClick={() => navigate('/customs/invoices/upload')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Upload your first invoice
            </button>
          </div>
        ) : (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <DndContext sensors={colSensors} collisionDetection={closestCenter} onDragEnd={handleColDragEnd}>
                <SortableContext items={colOrder} strategy={horizontalListSortingStrategy}>
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      {visibleCols.map(col => (
                        <SortableColHeader
                          key={col.id}
                          id={col.id}
                          className={`px-4 py-3 font-medium text-gray-600 dark:text-gray-300 ${COL_HEADER_ALIGN[col.id]}`}
                        >
                          {col.label}
                        </SortableColHeader>
                      ))}
                    </tr>
                  </thead>
                </SortableContext>
              </DndContext>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {invoices.map((inv: CommercialInvoice) => (
                  <tr
                    key={inv.id}
                    onClick={() => navigate(`/customs/invoices/${inv.id}`)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                  >
                    {visibleCols.map(col => {
                      const { className, content } = CELL_RENDERERS[col.id](inv)
                      return <td key={col.id} className={className}>{content}</td>
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
